/**
 * Orçamentos — propostas comerciais (Task 13, brief 3.4).
 *
 * Regras que esta tela respeita:
 *
 * 1. **Total e margem vêm dos ITENS**, recalculados por `quoteTotals` (regra
 *    pura), não de colunas agregadas. Se o resumo gravado e os itens
 *    discordarem, o que vale é o item — é o que o cliente recebeu na proposta.
 * 2. **Vencimento é derivado do prazo** (`effectiveQuoteStatus`), não de uma
 *    rotina que carimba a coluna: proposta vencida exibida como "enviada" faria
 *    o operador cobrar resposta de um preço que não vale mais.
 * 3. **Sem custo não há margem.** Item sem custo sincronizado deixa a margem da
 *    proposta indisponível e a tela diz isso, em vez de calcular sobre um custo
 *    inventado.
 * 4. **A proposta aceita navega até a venda** (`platform_order_id`): é o
 *    drill-down "orçamento → pedido" do mesmo cliente.
 *
 * Redesign R6 (apresentação apenas): wrapper e header no padrão aprovado; cada
 * bloco de itens (`quote-items`) vira cartão tokenizado com h3 `text-lg
 * font-semibold text-ink`; os avisos `.page__note` recebem `text-sm text-muted`.
 * Todos os `data-*` (incl. `data-selected-quote`, `data-quotes-scope`,
 * `data-quote-selected`, `data-quote-id`, `data-selected`), os textos e os
 * links são PRESERVADOS — as classes hook seguem no markup.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + ícones lucide (`FileText` no título da tela,
 * `Package` no bloco de itens de cada proposta, mesmo padrão do `SectionTitle`
 * de `PedidoDetalhe.tsx`) — esta tela não tinha nenhum ícone antes. Nenhum
 * `data-*`/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, Package } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { CrmBoundary } from "../components/CrmBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { isCrmSourceAwaitingContract, quoteView, type CrmDataset, type QuoteView } from "../crm/dataset.js";
import { QUOTE_STATUS_LABELS } from "../crm/pipeline.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatDateTime, formatPercent } from "../format.js";
import type { OrcamentosViewProps } from "../crm/view-props.js";
import type { CrmQuoteItem } from "../api/types.js";

function QuoteItemsTable({ quote }: { quote: QuoteView }): ReactElement {
  return (
    <RecordTable<CrmQuoteItem>
      bare
      caption={`Itens da proposta ${quote.quote.number}, com o preço travado na emissão`}
      rows={[...quote.quote.items].sort((a, b) => a.position - b.position)}
      idOf={(item) => item.id}
      emptyMessage="Proposta sem itens."
      // Fase 8 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
      animateRows
      columns={[
        {
          key: "sku",
          label: "Item",
          render: (item) => (item.productId ? <Link to={`/catalogo/${item.productId}`}>{item.sku}</Link> : item.sku)
        },
        { key: "description", label: "Descrição", render: (item) => item.description || "—" },
        { key: "quantity", label: "Qtd.", render: (item) => String(item.quantity) },
        { key: "unitPrice", label: "Preço unitário", render: (item) => formatBrl(item.unitPriceCents) },
        {
          key: "unitCost",
          label: "Custo unitário",
          render: (item) =>
            item.unitCostCents === null ? <span data-pending="true">custo não sincronizado</span> : formatBrl(item.unitCostCents)
        }
      ]}
    />
  );
}

export function OrcamentosView({ crm, now, customerId = null, quoteId = null }: OrcamentosViewProps): ReactElement {
  if (isCrmSourceAwaitingContract(crm, "quotes")) {
    return (
      <div className="page page--orcamentos flex flex-col gap-6" data-page="orcamentos">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Orçamentos</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Orçamentos ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/crm/quotes não respondeu neste ambiente. Lista vazia aqui diria que a operação não tem nenhuma proposta na mesa."
        />
      </div>
    );
  }
  const instant = now ?? new Date();
  const rows = crm.quotes
    .filter((quote) => customerId === null || quote.customerId === customerId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map((quote) => quoteView(quote, instant));
  // Proposta exata pedida pelo drill-down. Quando ela não está no recorte, a
  // tela DIZ isso: calar a divergência faria o operador ler a lista inteira
  // acreditando que a proposta que ele clicou está destacada em algum lugar.
  const selected = quoteId === null ? null : (rows.find((item) => item.quote.id === quoteId) ?? null);
  return (
    <div className="page page--orcamentos flex flex-col gap-6" data-page="orcamentos" {...(quoteId ? { "data-selected-quote": quoteId } : {})}>
      <header className="drilldown-header flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-2xl font-black text-ink sm:text-3xl">
          <FileText className="h-5 w-5 shrink-0 text-brand-text" aria-hidden focusable={false} />
          Orçamentos
        </h2>
        <p className="drilldown-header__origin text-sm text-muted" data-quotes-scope={customerId ?? "all"}>
          {customerId ? `${rows.length} proposta(s) do cliente ${customerId}.` : `${rows.length} proposta(s) registradas.`}
        </p>
        <Link to="/crm/orcamentos" className="drilldown-header__back text-sm text-brand-text hover:underline">
          Ver todas as propostas
        </Link>
      </header>
      {quoteId ? (
        selected ? (
          <p className="page__note text-sm text-muted" data-quote-selected={selected.quote.id}>
            {`Proposta ${selected.quote.number} destacada abaixo — é o registro de onde você veio.`}
          </p>
        ) : (
          <StatusPanel
            kind="empty"
            title={`A proposta ${quoteId} não está neste recorte`}
            detail="O link pediu uma proposta que não aparece na lista filtrada (pode ter sido removida do recorte do cliente). Nada abaixo está destacado."
          />
        )
      ) : null}
      <p className="page__note text-sm text-muted">
        Total e margem são recalculados dos itens (preço travado na emissão, custo copiado na emissão). A proposta
        aqui é comercial: documento fiscal continua sendo emitido no Bling.
      </p>
      <RecordTable<QuoteView>
        caption="Propostas comerciais com situação efetiva, total e margem"
        rows={rows}
        idOf={(item) => item.quote.id}
        emptyMessage="Nenhuma proposta neste recorte."
        animateRows
        selectedId={selected?.quote.id ?? null}
        columns={[
          { key: "number", label: "Proposta", render: (item) => item.quote.number },
          {
            key: "customer",
            label: "Cliente",
            render: (item) => <Link to={`/clientes/${item.quote.customerId}`}>{item.quote.customerId}</Link>
          },
          {
            key: "status",
            label: "Situação",
            render: (item) => (
              <span data-quote-status={item.status} data-quote-stored-status={item.quote.status}>
                {item.status === "expired" && item.quote.status !== "expired"
                  ? `${QUOTE_STATUS_LABELS[item.status]} (prazo vencido)`
                  : QUOTE_STATUS_LABELS[item.status]}
              </span>
            )
          },
          { key: "subtotal", label: "Subtotal", render: (item) => formatBrl(item.totals.subtotalCents) },
          { key: "discount", label: "Desconto", render: (item) => formatBrl(item.totals.discountCents) },
          {
            key: "total",
            label: "Total",
            render: (item) =>
              item.totals.totalCents === null ? <span data-pending="true">sem frete cotado</span> : formatBrl(item.totals.totalCents)
          },
          {
            key: "margin",
            label: "Margem",
            render: (item) =>
              item.totals.marginPct === null ? <span data-pending="true">custo não sincronizado</span> : formatPercent(item.totals.marginPct)
          },
          { key: "owner", label: "Responsável", render: (item) => item.quote.ownerLabel },
          {
            key: "validUntil",
            label: "Validade",
            render: (item) => (item.quote.validUntil ? formatDateTime(item.quote.validUntil) : "sem prazo declarado")
          },
          {
            key: "order",
            label: "Venda",
            render: (item) =>
              item.quote.platformOrderId ? <Link to={`/pedidos/${item.quote.platformOrderId}`}>ver pedido gerado</Link> : "ainda não virou pedido"
          }
        ]}
      />
      {/* Itens de cada proposta do recorte: é o que torna o total auditável sem
          abrir outra tela (o número e os registros que o explicam no mesmo lugar). */}
      {rows.map((item) => (
        <section
          key={item.quote.id}
          className={
            "quote-items flex flex-col gap-3 rounded-panel border bg-surface p-4 shadow-s1 " +
            (selected && selected.quote.id === item.quote.id ? "border-brand ring-1 ring-inset ring-brand" : "border-border")
          }
          data-quote-id={item.quote.id}
          {...(selected && selected.quote.id === item.quote.id ? { "data-selected": "true", "aria-current": "true" as const } : {})}
        >
          <h3 className="flex items-center gap-2 text-lg font-extrabold text-ink">
            <Package className="h-4 w-4 shrink-0 text-brand-text" aria-hidden focusable={false} />
            {`Itens de ${item.quote.number}`}
          </h3>
          <QuoteItemsTable quote={item} />
        </section>
      ))}
    </div>
  );
}

export function Orcamentos(): ReactElement {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("customer");
  // `quote=` é o id da proposta de onde o operador veio (drill-down do 360).
  const quoteId = searchParams.get("quote");
  return <CrmBoundary>{(crm: CrmDataset) => <OrcamentosView crm={crm} customerId={customerId} quoteId={quoteId} />}</CrmBoundary>;
}
